import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Paper,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    CircularProgress,
} from '@mui/material';
import { ContentContainer } from '../ContentContainer';
import { HeaderInfo } from '../HeaderInfo/HeaderInfo';
import { useHistory } from 'react-router';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import locationStyles from '../../app/location-management/LocationStyle';
import { pushRate } from '../../constants/device-data';
import deviceStyles from '../../app/device-management/DeviceStyle';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { StarfishDeviceData } from '../../types/device';
import { IP_ADDRESS, RESTRICT_BLG_DEVICE_SPACE_REGEX, RESTRICT_SPEC_WITHOUT_SPACE_REGEX } from '../../constants';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { Actions, Thunks } from '../../actions';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../AuthContextProvider';

export const EditStarfishConnectedDevice: React.FC<{
    gatewayDetails: StarfishDeviceData | null;
    updateGatewayDeviceData: Function;
}> = (props) => {
    const history = useHistory();
    const currentAppRoute = useCurrentAppRoute();
    const [ipAddressError, setIPAddressError] = React.useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [unmodifiedDeviceInfo, setUnmodifiedDeviceInfo] = useState<StarfishDeviceData | null>(null);
    const [modifiedDeviceInfo, setModifiedDeviceInfo] = useState<StarfishDeviceData | null>(null);
    const deviceClasses = deviceStyles();
    const { t } = useLanguageLocale();
    const classes = locationStyles();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const dispatch = useDispatch();

    const selectedOrganization = useSelectedOrganizationData();
    const gatewayDeviceState = useSelector(Selectors.StarfishDevice.deviceRegistration);

    const navigateToConnectedDeviceProfile = (): void => {
        if (currentAppRoute.type === 'EditStarfishConnectedDevice')
            pushAppRoute(history, {
                type: 'StarfishDeviceProfile',
                deviceId: currentAppRoute.deviceId,
                locationId: currentAppRoute.locationId,
                orgId: currentAppRoute.orgId,
                siteId: currentAppRoute.siteId,
            });
    };

    useEffect(() => {
        if (props.gatewayDetails) {
            setUnmodifiedDeviceInfo(JSON.parse(JSON.stringify(props.gatewayDetails)));
            setModifiedDeviceInfo(props.gatewayDetails);
        }
    }, [props.gatewayDetails]);

    useEffect(() => {
        if (!gatewayDeviceState.starfishgatewayLoading && gatewayDeviceState.starfishgatewaySuccess) {
            addToast('Connected Device updated successfully.');
            dispatch(Actions.StarfishDevices.StarfishGatewayDeviceUpdateReset());
            navigateToConnectedDeviceProfile();
        }
    }, [gatewayDeviceState.starfishgatewayLoading, gatewayDeviceState.starfishgatewaySuccess]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedDeviceInfo) !== JSON.stringify(modifiedDeviceInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedDeviceInfo, modifiedDeviceInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    const onChangeValues = (value: string | number, type: string): void => {
        const deviceObj: any = { ...modifiedDeviceInfo };
        deviceObj[`${type}`] = value;
        setModifiedDeviceInfo(deviceObj);
    };
    const onChangeDeviceIP = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value !== '' && IP_ADDRESS.test(e.target.value)) {
            setIPAddressError(false);
        } else {
            setIPAddressError(true);
        }
        const deviceObj: any = { ...modifiedDeviceInfo };
        deviceObj.deviceIP = e.target.value;
        setModifiedDeviceInfo(deviceObj);
    };

    const onChangeDeviceName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (RESTRICT_BLG_DEVICE_SPACE_REGEX.test(e.target.value) && modifiedDeviceInfo) {
            onChangeValues(e.target.value, 'name');
        }
    };

    const onChangeSerial = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if ((e.target.value === '' || RESTRICT_SPEC_WITHOUT_SPACE_REGEX.test(e.target.value)) && modifiedDeviceInfo) {
            onChangeValues(e.target.value, 'serial');
        }
    };
    const onChangeMac = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || RESTRICT_SPEC_WITHOUT_SPACE_REGEX.test(e.target.value)) {
            onChangeValues(e.target.value, 'mac');
        }
    };

    const onChangeValuesPushRate = (event: any): void => {
        if (event.target.value !== '') {
            onChangeValues(event.target.value === '' ? '' : Number(event.target.value), 'pushRate');
        }
    };

    const isDisable = (): boolean => {
        if (!gatewayDeviceState.starfishgatewayLoading && !ipAddressError && modifiedDeviceInfo?.name !== '') {
            return !(modifiedDeviceInfo && isDirty);
        }
        return true;
    };

    const onSubmit = (): void => {
        setIsDirty(false);
        if (modifiedDeviceInfo && isRecordFound(selectedOrganization)) {
            const updateddeviceObj: StarfishDeviceData = { ...modifiedDeviceInfo };
            setModifiedDeviceInfo(updateddeviceObj);
            dispatch(
                Thunks.StarfishDevice.StarfishGatewayDeviceUpdate(
                    modifiedDeviceInfo.deviceId,
                    modifiedDeviceInfo.name,
                    modifiedDeviceInfo.siteId,
                    modifiedDeviceInfo.deviceIP || '',
                    authHelper,
                    addToast,
                    modifiedDeviceInfo.mac,
                    modifiedDeviceInfo.serial,
                    modifiedDeviceInfo.pushRate || 0
                )
            );
            props.updateGatewayDeviceData(updateddeviceObj);
        }
    };

    return (
        <>
            <ContentContainer>
                <Paper style={{ padding: 48 }}>
                    <HeaderInfo
                        title="Edit Starfish Connected Device Information"
                        entity="when editing a Connected Device"
                    />
                    {props.gatewayDetails && modifiedDeviceInfo && (
                        <Box style={{ padding: 8 }}>
                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="name"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    fullWidth
                                    disabled
                                    label="Name"
                                    data-testid="name"
                                    helperText={
                                        <Box component="span" display="flex" justifyContent="flex-end">
                                            {modifiedDeviceInfo.name.length}/24
                                        </Box>
                                    }
                                    value={modifiedDeviceInfo.name}
                                    inputProps={{ minLength: 1, maxLength: 24, 'data-testid': 'name' }}
                                    onChange={(e: any): void => {
                                        onChangeDeviceName(e);
                                    }}
                                />{' '}
                            </Box>

                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="deviceIp"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    label="Device IP"
                                    required
                                    data-testid="deviceIp"
                                    error={ipAddressError}
                                    helperText={ipAddressError ? 'Invalid Device IP Address' : ''}
                                    value={modifiedDeviceInfo.deviceIP}
                                    inputProps={{ maxLength: 50 }}
                                    onChange={(e): void => {
                                        onChangeDeviceIP(e);
                                    }}
                                />
                            </Box>
                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="serial"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    label="Serial"
                                    data-testid="serial"
                                    value={modifiedDeviceInfo.serial}
                                    inputProps={{ maxLength: 255, 'data-testid': 'serial' }}
                                    onChange={(e: any): void => {
                                        onChangeSerial(e);
                                    }}
                                />
                            </Box>
                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="mac"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    label="Mac"
                                    data-testid="mac"
                                    value={modifiedDeviceInfo.mac}
                                    inputProps={{ maxLength: 255 }}
                                    onChange={(e): void => {
                                        onChangeMac(e);
                                    }}
                                />
                            </Box>
                            <Box className={classes.padding8}>
                                <FormControl
                                    variant="filled"
                                    style={{ marginTop: 10 }}
                                    required
                                    className={`${deviceClasses.fullWidth}`}
                                >
                                    <InputLabel id="standardData" data-testid="standard-label-dd">
                                        Publish Rate
                                    </InputLabel>
                                    <Select
                                        label="pushRate"
                                        value={modifiedDeviceInfo.pushRate}
                                        inputProps={{ 'data-testid': 'pushRate-dd', width: '100%' }}
                                        onChange={(event: any): void => {
                                            onChangeValuesPushRate(event);
                                        }}
                                    >
                                        {pushRate.map((data: { key: string; value: string }, index: number) => (
                                            <MenuItem key={index} value={data.key} data-testid={`pushRate${index}`}>
                                                {data.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    )}
                    <Box
                        className={classes.iconRightEnd}
                        style={{ display: 'flex', justifyContent: 'flex-end', padding: '24px 8px' }}
                    >
                        <Button
                            style={{ marginRight: 16 }}
                            variant="outlined"
                            color="primary"
                            data-testid="cancel-device"
                            onClick={navigateToConnectedDeviceProfile}
                        >
                            {t('ACTIONS.CANCEL')}
                        </Button>
                        <Button
                            startIcon={
                                gatewayDeviceState.starfishgatewayLoading ? (
                                    <CircularProgress size="1rem" style={{ color: 'white' }} />
                                ) : (
                                    <></>
                                )
                            }
                            variant="contained"
                            disabled={isDisable()}
                            color="primary"
                            data-testid="edit-device-button"
                            onClick={onSubmit}
                        >
                            {t('ACTIONS.SUBMITCHANGES')}
                        </Button>
                    </Box>
                </Paper>
            </ContentContainer>
            <NavigationPromptModal isDirty={isDirty} />
        </>
    );
};
